import { Alert02Icon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { Button } from '../primitives/button.js';
import { Dialog } from '../primitives/dialog.js';
import { CopyField } from './copy-field.js';

export interface OneTimeSecret {
  label: string;
  value: string;
  description?: React.ReactNode;
  /** false for non-secret companions (e.g. an ingress URL): shown in clear. Defaults to true. */
  secret?: boolean;
}

export interface OneTimeSecretDialogProps {
  /** null/empty closes the dialog. */
  secrets: readonly OneTimeSecret[] | null;
  onClose: () => void;
  title: string;
  description?: React.ReactNode;
  /** Prominent warning (e.g. "Los secretos anteriores dejaron de ser válidos"). */
  warning?: React.ReactNode;
  /** Extra content above the fields (destination, instructions). */
  children?: React.ReactNode;
  acknowledgeLabel?: string;
}

/**
 * Displays credentials exactly once after creating or rotating them. Not dismissible by Escape/outside click:
 * the person must confirm they saved them.
 */
export function OneTimeSecretDialog({
  secrets,
  onClose,
  title,
  description = 'Cópialo ahora. Por seguridad no se volverá a mostrar; si lo pierdes tendrás que rotarlo.',
  warning,
  children,
  acknowledgeLabel = 'Ya lo guardé',
}: OneTimeSecretDialogProps) {
  const open = !!secrets && secrets.length > 0;
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      dismissible={false}
      size="lg"
      title={title}
      description={description}
      footer={<Button onClick={onClose}>{acknowledgeLabel}</Button>}
    >
      {open ? (
        <div className="grid gap-4">
          {warning ? (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-control bg-warning-bg px-3 py-2 text-body text-warning"
            >
              <Icon icon={Alert02Icon} aria-hidden className="mt-0.5 size-4 shrink-0" />
              <span>{warning}</span>
            </p>
          ) : null}
          {children}
          {secrets.map((s) => (
            <CopyField
              key={s.label}
              label={s.label}
              value={s.value}
              secret={s.secret ?? true}
              description={s.description}
            />
          ))}
        </div>
      ) : null}
    </Dialog>
  );
}
